import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { FirefoxWarning } from "./components/FirefoxWarning";
import { ThemeProvider } from "next-themes";
import WhatsAppButton from "./components/WhatsAppButton";

// Redirect dal vecchio dominio emergent.host al nuovo dominio personalizzato
// Disabilitato per compatibilità deployment - il redirect viene gestito a livello DNS/Ingress
// if (window.location.hostname === "odr-academy.emergent.host") {
//   window.location.replace("https://www.odr-academy.it" + window.location.pathname + window.location.search);
// }

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RegisterBenessere from "./pages/RegisterBenessere";
import DashboardPage from "./pages/DashboardPage";
import SessionsPage from "./pages/SessionsPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import MyCalendarPage from "./pages/MyCalendarPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCoursesPage from "./pages/AdminCoursesPage";
import AdminSessionsPage from "./pages/AdminSessionsPage";
import AdminBookingsPage from "./pages/AdminBookingsPage";
import AdminDocentiPage from "./pages/AdminDocentiPage";
import AdminAssociationsPage from "./pages/AdminAssociationsPage";
import AdminPersonalizationPage from "./pages/AdminPersonalizationPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminMaterialsPage from "./pages/AdminMaterialsPage";
import MyMaterialsPage from "./pages/MyMaterialsPage";
import AdminReferentiPage from "./pages/AdminReferentiPage";
import QuizPage from "./pages/QuizPage";
import AdminQuizResponsesPage from "./pages/AdminQuizResponsesPage";
import MaterialiDocente from "./pages/MaterialiDocente";
import MaterialiClienti from "./pages/MaterialiClienti";
import AdminAccademiaPage from "./pages/AdminAccademiaPage";
import AccademiaLandingPage from "./pages/AccademiaLandingPage";
import AcademyBenessereLandingPage from "./pages/AcademyBenessereLandingPage";
import LongevityAcademyPage from "./pages/LongevityAcademyPage";
import LongevityCorporatePage from "./pages/LongevityCorporatePage";
import LongevityVO2MaxPage from "./pages/LongevityVO2MaxPage";
import PresentazioneBanchePage from "./pages/PresentazioneBanchePage";
import { HomeV2, MetodoV2, PercorsoV2, RespirazioneV2, DatiV2, AziendeV2, AcademyV2, FaqV2, IscrivitiV2 } from "./pages/LongevityV2Pages";
import LeadFinderPage from "./pages/LeadFinderPage";
import BotanicalOxygenCarePage from "./pages/BotanicalOxygenCarePage";import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminComunicazioniPage from "./pages/AdminComunicazioniPage";
import AdminSoleaKnowledgePage from "./pages/AdminSoleaKnowledgePage";
import ContattiMarketingPage from "./pages/ContattiMarketingPage";
import WhatsAppAutomationPage from "./pages/WhatsAppAutomationPage";
import DocumentiContabiliPage from "./pages/DocumentiContabiliPage";
import AcquisizioneContattiFieraPage from "./pages/AcquisizioneContattiFieraPage";
import AIContentEnginePage from "./pages/AIContentEnginePage";
import NewsletterManagementPage from "./pages/NewsletterManagementPage";
import AdminTecnichePage from "./pages/AdminTecnichePage";
import AdminPotenzialiClientiPage from "./pages/AdminPotenzialiClientiPage";
import MammouthAIPage from "./pages/MammouthAIPage";
import DiventaPartnerPage from "./pages/DiventaPartnerPage";
import HotelSpaPartnerPage from "./pages/HotelSpaPartnerPage";
import OdrNetworkMapPage from "./pages/OdrNetworkMapPage";
import PublicNetworkMap from "./pages/PublicNetworkMap";
import WarehousePage from "./pages/WarehousePage";
import MagazzinoOperatorePage from "./pages/MagazzinoOperatorePage";
import FeedbackCampaignPage from "./pages/FeedbackCampaignPage";
import BachecaPublicPage from "./pages/BachecaPublicPage";
import AdminBachecaPage from "./pages/AdminBachecaPage";
import AdminMappaFunzionalitaPage from "./pages/AdminMappaFunzionalitaPage";
import CrmPromemoriaConfigPage from "./pages/CrmPromemoriaConfigPage";
import CrmInvioMiratoPage from "./pages/CrmInvioMiratoPage";
import { CrmDashboardPage, CrmClientiPage, CrmClienteDetailPage, CrmOrdiniInboxPage, CrmImportPage, CrmOutlookConfigPage } from "./pages/CrmPages";
import OdrStoriesPage from "./pages/OdrStoriesPage";
import OdrStoriesCommunityPage from "./pages/OdrStoriesCommunityPage";
import AdminTelegramPage from "./pages/AdminTelegramPage";
import EasterEmailPage from "./pages/EasterEmailPage";
import VideoGeneratorPage from "./pages/VideoGeneratorPage";
import SkinAnalysisPage from "./pages/SkinAnalysisPage";
import PreventionTechPage from "./pages/PreventionTechPage";
import ProtocolloCabinaLandingPage from "./pages/ProtocolloCabinaLandingPage";
import BlogManagementPage from "./pages/BlogManagementPage";
import VerificaEmailPage from "./pages/VerificaEmailPage";
import BenessereClubPage from "./pages/BenessereClubPage";
import ProBenessereClubPage from "./pages/ProBenessereClubPage";

// Public Pages - La Scienza del Benessere Applicata
import ScienzaBenessereHome from "./pages/ScienzaBenessere/ScienzaBenessereHome";
import ArticleListPage from "./pages/ScienzaBenessere/ArticleListPage";
import ArticleDetailPage from "./pages/ScienzaBenessere/ArticleDetailPage";
import TecnicheBenessere from "./pages/ScienzaBenessere/TecnicheBenessere";

// Landing Page - Scelta percorso
import LandingPage from "./pages/LandingPage";

// Floating "Home" button per i clienti Accademia
import HomeFab from "./components/HomeFab";

// Redirect per URL brandizzati → file HTML statici
import ExternalRedirect from "./components/ExternalRedirect";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, allowOperatoreFiera = false, allowedRoles = null }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center hero-gradient">
        <div className="animate-pulse text-primary">Caricamento...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Operatore fiera può accedere solo a pagine specifiche
  if (user.role === "operatore_fiera" && !allowOperatoreFiera) {
    return <Navigate to="/admin/contatti-fiera" replace />;
  }
  
  if (adminOnly && !["admin", "docente", "operatore_fiera"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  // Permessi per ruoli arbitrari (es. CRM: ["admin","direzione","agente"])
  if (allowedRoles && Array.isArray(allowedRoles) && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system">
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
          <Routes>
            {/* PUBLIC STANDALONE PAGES - Priorita massima, prima di tutto */}
            <Route path="/prevenzione" element={<PreventionTechPage />} />
            <Route path="/protocollo-cabina" element={<ProtocolloCabinaLandingPage />} />
            <Route path="/protocollo-parrucchieri" element={<ProtocolloCabinaLandingPage />} />
            <Route path="/protocollo-cabina-inverno" element={<ProtocolloCabinaLandingPage />} />
            <Route path="/protocollo-parrucchieri-inverno" element={<ProtocolloCabinaLandingPage />} />
            <Route path="/academy-benessere-integrato" element={<AcademyBenessereLandingPage />} />
            <Route path="/academy-benessere" element={<AcademyBenessereLandingPage />} />
            <Route path="/longevity-academy" element={<LongevityAcademyPage />} />
            <Route path="/longevity" element={<LongevityAcademyPage />} />
            <Route path="/longevity/aziende" element={<LongevityCorporatePage />} />
            <Route path="/longevity/corporate" element={<LongevityCorporatePage />} />
            <Route path="/longevity/vo2max" element={<LongevityVO2MaxPage />} />
            <Route path="/longevity/presentazione-banche" element={<PresentazioneBanchePage />} />
            <Route path="/longevity/banche" element={<PresentazioneBanchePage />} />
            <Route path="/longevity/individuale" element={<LongevityAcademyPage />} />
            <Route path="/v2" element={<HomeV2 />} />
            <Route path="/v2/metodo" element={<MetodoV2 />} />
            <Route path="/v2/percorso" element={<PercorsoV2 />} />
            <Route path="/v2/respirazione" element={<RespirazioneV2 />} />
            <Route path="/v2/dati" element={<DatiV2 />} />
            <Route path="/v2/aziende" element={<AziendeV2 />} />
            <Route path="/v2/academy" element={<AcademyV2 />} />
            <Route path="/v2/faq" element={<FaqV2 />} />
            <Route path="/v2/iscriviti" element={<IscrivitiV2 />} />
            <Route path="/admin/leadfinder" element={<LeadFinderPage />} />
            <Route path="/botanical-oxygen-care" element={<BotanicalOxygenCarePage />} />
            <Route path="/botanical" element={<BotanicalOxygenCarePage />} />
            <Route path="/embed/network-map" element={<PublicNetworkMap />} />
            <Route path="/bacheca" element={<BachecaPublicPage />} />
            
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/registrazione-benessere" element={<RegisterBenessere />} />
            <Route path="/accademia" element={<AccademiaLandingPage />} />
            <Route path="/accademia/iscrizione" element={<AccademiaLandingPage />} />
            <Route path="/diventa-partner" element={<DiventaPartnerPage />} />
            <Route path="/verifica-email" element={<VerificaEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Landing Pages - Iscrizione con descrizione completa */}
            <Route path="/club" element={<BenessereClubPage />} />
            <Route path="/pro" element={<ProBenessereClubPage />} />
            
            {/* Landing Page - Scelta del percorso */}
            <Route path="/" element={<HomeV2 />} />
            <Route path="/old-home" element={<LandingPage />} />

            {/* Shortcut URL per landing statiche (Mabella Fest) */}
            <Route path="/mabella" element={<ExternalRedirect to="/mabella-fest.html" />} />
            <Route path="/mabella-fest" element={<ExternalRedirect to="/mabella-fest.html" />} />
            <Route path="/festival" element={<ExternalRedirect to="/mabella-fest.html" />} />
            <Route path="/mabellafest" element={<ExternalRedirect to="/mabella-fest.html" />} />
            <Route path="/flipbook" element={<ExternalRedirect to="/flipbook.html" />} />
            <Route path="/brochure" element={<ExternalRedirect to="/flipbook.html" />} />
            
            <Route path="/sessioni" element={
              <ProtectedRoute>
                <SessionsPage />
              </ProtectedRoute>
            } />
            <Route path="/corsi" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/corso/:courseId" element={
              <ProtectedRoute>
                <CourseDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/calendario" element={
              <ProtectedRoute>
                <MyCalendarPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/corsi" element={
              <ProtectedRoute adminOnly>
                <AdminCoursesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/sessioni" element={
              <ProtectedRoute adminOnly>
                <AdminSessionsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/iscrizioni" element={
              <ProtectedRoute adminOnly>
                <AdminBookingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/utenti" element={
              <ProtectedRoute adminOnly>
                <AdminUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/docenti" element={
              <ProtectedRoute adminOnly>
                <AdminDocentiPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/associazioni" element={
              <ProtectedRoute adminOnly>
                <AdminAssociationsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/personalizzazione" element={
              <ProtectedRoute adminOnly>
                <AdminPersonalizationPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/materiali" element={
              <ProtectedRoute adminOnly>
                <AdminMaterialsPage />
              </ProtectedRoute>
            } />
            <Route path="/materiali" element={
              <ProtectedRoute>
                <MyMaterialsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/referenti" element={
              <ProtectedRoute adminOnly>
                <AdminReferentiPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/quiz-risposte" element={
              <ProtectedRoute adminOnly>
                <AdminQuizResponsesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/comunicazioni" element={
              <ProtectedRoute adminOnly>
                <AdminComunicazioniPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/bacheca" element={
              <ProtectedRoute adminOnly>
                <AdminBachecaPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/mappa" element={
              <ProtectedRoute adminOnly>
                <AdminMappaFunzionalitaPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/promemoria" element={
              <ProtectedRoute adminOnly>
                <CrmPromemoriaConfigPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/invio-mirato" element={
              <ProtectedRoute adminOnly>
                <CrmInvioMiratoPage />
              </ProtectedRoute>
            } />
            {/* CRM ITA318 */}
            <Route path="/admin/crm" element={
              <ProtectedRoute allowedRoles={["admin","direzione","docente","agente"]}>
                <CrmDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/clienti" element={
              <ProtectedRoute allowedRoles={["admin","direzione","docente","agente"]}>
                <CrmClientiPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/clienti/:cliente_id" element={
              <ProtectedRoute allowedRoles={["admin","direzione","docente","agente"]}>
                <CrmClienteDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/ordini" element={
              <ProtectedRoute adminOnly>
                <CrmOrdiniInboxPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/import" element={
              <ProtectedRoute adminOnly>
                <CrmImportPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/crm/outlook" element={
              <ProtectedRoute adminOnly>
                <CrmOutlookConfigPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/solea" element={
              <ProtectedRoute adminOnly>
                <AdminSoleaKnowledgePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing" element={
              <ProtectedRoute adminOnly>
                <ContattiMarketingPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/whatsapp" element={
              <ProtectedRoute adminOnly>
                <WhatsAppAutomationPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/documenti-contabili" element={
              <ProtectedRoute adminOnly>
                <DocumentiContabiliPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/contatti-fiera" element={
              <ProtectedRoute adminOnly allowOperatoreFiera>
                <AcquisizioneContattiFieraPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/ai-engine" element={
              <ProtectedRoute adminOnly>
                <AIContentEnginePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/newsletter" element={
              <ProtectedRoute adminOnly>
                <NewsletterManagementPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/tecniche-benessere" element={
              <ProtectedRoute adminOnly>
                <AdminTecnichePage />
              </ProtectedRoute>
            } />
            <Route path="/admin/potenziali-clienti" element={
              <ProtectedRoute adminOnly>
                <AdminPotenzialiClientiPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/accademia" element={
              <ProtectedRoute adminOnly>
                <AdminAccademiaPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/mammouth-ai" element={
              <ProtectedRoute adminOnly>
                <MammouthAIPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/hotel-spa" element={
              <ProtectedRoute adminOnly>
                <HotelSpaPartnerPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/network-map" element={
              <ProtectedRoute adminOnly>
                <OdrNetworkMapPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/magazzino" element={
              <ProtectedRoute adminOnly>
                <WarehousePage />
              </ProtectedRoute>
            } />
            <Route path="/magazzino" element={<MagazzinoOperatorePage />} />
            <Route path="/admin/feedback-campagna" element={
              <ProtectedRoute adminOnly>
                <FeedbackCampaignPage />
              </ProtectedRoute>
            } />
            <Route path="/stories" element={<OdrStoriesPage />} />
            <Route path="/stories/community" element={<OdrStoriesCommunityPage />} />
            <Route path="/admin/stories" element={
              <ProtectedRoute adminOnly>
                <OdrStoriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/telegram" element={
              <ProtectedRoute adminOnly>
                <AdminTelegramPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/easter-email" element={
              <ProtectedRoute adminOnly>
                <EasterEmailPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/video-generator" element={
              <ProtectedRoute adminOnly>
                <VideoGeneratorPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/skin-analysis" element={
              <ProtectedRoute adminOnly>
                <SkinAnalysisPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/blog-wp" element={
              <ProtectedRoute adminOnly>
                <BlogManagementPage />
              </ProtectedRoute>
            } />
            <Route path="/quiz" element={
              <ProtectedRoute>
                <QuizPage />
              </ProtectedRoute>
            } />
            <Route path="/materiali-docente" element={
              <ProtectedRoute>
                <MaterialiDocente />
              </ProtectedRoute>
            } />
            <Route path="/biblioteca" element={
              <ProtectedRoute>
                <MaterialiClienti />
              </ProtectedRoute>
            } />
            
            {/* PUBLIC ROUTES - La Scienza del Benessere Applicata */}
            <Route path="/benessere" element={<ScienzaBenessereHome />} />
            <Route path="/benessere/articoli" element={<ArticleListPage />} />
            <Route path="/benessere/categoria/:sectorId" element={<ArticleListPage />} />
            <Route path="/benessere/articolo/:articleId" element={<ArticleDetailPage />} />
            <Route path="/benessere/tecniche" element={<TecnicheBenessere />} />
            <Route path="/benessere/tecniche/:categoria" element={<TecnicheBenessere />} />
            
            {/* CATCH-ALL: redirect to home per route non trovate */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
          <FirefoxWarning />
          <WhatsAppButton />
          <HomeFab />
        </BrowserRouter>
      </SiteSettingsProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
